<template>
  <div class="post-card">
    <!-- Corner brackets -->
    <div class="corner tl"></div>
    <div class="corner br"></div>

    <!-- ── HEADER ── -->
    <div class="post-hd">
      <div class="post-user">
        <div class="avatar" @click="goToProfile">
          <img v-if="post.userImageURL" :src="post.userImageURL" :alt="post.fullName" />
          <span v-else>{{ (post.fullName || '?').charAt(0) }}</span>
        </div>
        <div>
          <p class="post-author" @click="goToProfile">{{ post.fullName || 'Anonymous' }}</p>
          <p class="post-time">{{ post.timeAgo }}</p>
        </div>
      </div>

      <!-- Owner menu -->
      <div v-if="post.uid === currentUserUid" class="menu-wrap">
        <button class="menu-btn" @click="menuOpen = !menuOpen">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
        </button>
        <div v-if="menuOpen" class="menu-dropdown">
          <button class="menu-item" @click="toggleSaved">{{ post.saved ? 'Unsave (let it expire normally)' : 'Save (keep this permanently)' }}</button>
          <button class="menu-item" @click="editPost">Edit Travel Plan</button>
          <button class="menu-item menu-item-danger" @click="deletePost">Delete Travel Plan</button>
        </div>
      </div>

      <!-- Non-owner menu: report -->
      <div v-else class="menu-wrap">
        <button class="menu-btn" @click="menuOpen = !menuOpen">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
        </button>
        <div v-if="menuOpen" class="menu-dropdown">
          <button v-if="!reportSubmitted" class="menu-item menu-item-danger" @click="reportMenuOpen = true; menuOpen = false">Report Post</button>
          <span v-else class="menu-item menu-item-disabled">Reported ✓</span>
        </div>
      </div>

      <!-- Report reason picker -->
      <v-dialog v-model="reportMenuOpen" max-width="360">
        <div class="report-panel">
          <p class="report-title">Report this post</p>
          <button v-for="reason in reportReasons" :key="reason" class="report-reason-btn" @click="submitReport(reason)">
            {{ reason }}
          </button>
          <button class="report-cancel-btn" @click="reportMenuOpen = false">Cancel</button>
        </div>
      </v-dialog>
    </div>

    <div class="post-divider"></div>

    <!-- ── TRAVEL HEADER ── -->
    <div class="travel-hd">
      <div class="travel-tags">
        <span class="travel-tag travel-tag-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          Traveling
        </span>
        <span class="travel-tag travel-tag-city">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
          {{ post.city || 'Unknown City' }}
        </span>
      </div>
      <p class="travel-name">{{ hasDatePassed ? `Was at ${post.city || 'a new city'}` : `Heading to ${post.city || 'a new city'}` }}</p>
      <p v-if="post.travelEndDate" class="travel-dates">Visible until {{ formattedReturnDate }}</p>
      <p v-else-if="post.travelDates" class="travel-dates">{{ post.travelDates }}</p>
      <p v-if="post.saved" class="travel-saved">★ Saved — won't expire</p>
    </div>

    <!-- ── BODY ── -->
    <div class="post-body">
      <p v-if="post.text" class="post-text">{{ post.text }}</p>
      <div v-if="mediaItems.length" class="media-gallery" :class="{ single: mediaItems.length === 1 }">
        <div v-for="(item, idx) in mediaItems" :key="idx" class="media-gallery-item">
          <img
            v-if="item.type === 'image'"
            :src="item.url"
            alt="Post image"
            @click="openImageViewer(item.url)"
          />
          <video v-else controls :src="item.url"></video>
        </div>
      </div>
    </div>

    <div class="post-divider"></div>

    <!-- ── ACTIONS ── -->
    <div class="post-footer">
      <button class="action-btn action-btn-primary" @click="showComments = true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
        <span>Give a Recommendation</span>
      </button>
      <button class="action-btn action-btn-count" @click="showComments = true">
        <span>{{ post.comments || 0 }}</span>
      </button>
    </div>

    <!-- ── COMMENT SHEET ── -->
    <CommentSheet
      :postID="post.id"
      collection-name="explore"
      :show="showComments"
      @close="showComments = false"
    />

    <!-- ── IMAGE VIEWER OVERLAY ── -->
    <!-- Was a hand-rolled <Teleport to="body"><div style="position:fixed">.
         v-dialog's fullscreen mode positions correctly against the true
         viewport on its own — matches the working pattern in School
         Connect's ExplorePostCard.vue. -->
    <v-dialog v-model="imageDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <div class="tin-media-lightbox" @click.self="imageDialog = false">
        <img :src="selectedImage" class="tin-media-lightbox-img" alt="Full size" />
        <div class="tin-media-lightbox-actions">
          <button class="tin-media-lightbox-btn" @click="downloadImage(selectedImage)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
          <button class="tin-media-lightbox-btn" @click="imageDialog = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import CommentSheet from '@/components/CommentSheet.vue'

export default {
  name: 'TravelPlanCard',

  props: {
    post: { type: Object, required: true },
    currentUserUid: { type: String, default: null },
  },

  emits: ['delete-post', 'edit-post', 'report-post', 'toggle-saved'],

  components: { CommentSheet },

  data() {
    return {
      showComments: false,
      menuOpen: false,
      reportMenuOpen: false,
      reportSubmitted: false,
      reportReasons: ['Spam', 'Inappropriate content', 'Harassment', 'False information', 'Other'],
      nowTick: Date.now(), // ticks periodically so this card flips to past tense live, not just on next reload
      // Same image viewer as ExplorePostCard.vue — this card previously had
      // no click handler and no overlay at all on its gallery images,
      // which is why clicking a photo here did nothing whatsoever.
      imageDialog: false,
      selectedImage: '',
    }
  },

  mounted() {
    this._tenseTickInterval = setInterval(() => { this.nowTick = Date.now() }, 15000)
  },

  beforeUnmount() {
    clearInterval(this._tenseTickInterval)
  },

  computed: {
    mediaItems() {
      if (Array.isArray(this.post.media) && this.post.media.length) return this.post.media
      const legacy = []
      if (this.post.imageURL) legacy.push({ url: this.post.imageURL, type: 'image' })
      if (this.post.videoURL) legacy.push({ url: this.post.videoURL, type: 'video' })
      return legacy
    },

    formattedReturnDate() {
      if (!this.post.travelEndDate) return ''
      const d = this.post.travelEndDate?.toDate
        ? this.post.travelEndDate.toDate()
        : new Date(this.post.travelEndDate)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleString(undefined, { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },

    hasDatePassed() {
      this.nowTick // eslint-disable-line no-unused-expressions
      const now = new Date()
      if (this.post.travelEndDate) {
        const d = this.post.travelEndDate?.toDate
          ? this.post.travelEndDate.toDate()
          : new Date(this.post.travelEndDate)
        return !isNaN(d.getTime()) && now > d
      }
      const posted = this.post.timestamp?.toDate
        ? this.post.timestamp.toDate()
        : (this.post.timestamp ? new Date(this.post.timestamp) : null)
      if (!posted) return false
      return now - posted > 7 * 24 * 60 * 60 * 1000
    },
  },

  methods: {
    goToProfile() {
      if (this.post.uid) {
        this.$router.push({ name: 'MemberDetail', params: { id: this.post.uid } })
      }
    },

    openImageViewer(url) {
      this.selectedImage = url
      this.imageDialog = true
    },

    downloadImage(url) {
      const link = document.createElement('a')
      link.href = url
      link.download = 'image.jpg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    toggleSaved() {
      this.menuOpen = false
      this.$emit('toggle-saved', this.post)
    },

    deletePost() {
      this.menuOpen = false
      this.$emit('delete-post', this.post.id)
    },

    editPost() {
      this.menuOpen = false
      this.$emit('edit-post', this.post)
    },

    submitReport(reason) {
      this.reportMenuOpen = false
      this.reportSubmitted = true
      this.$emit('report-post', { post: this.post, reason })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

/* ── CARD ── */
.post-card { border: 1px solid rgba(140,180,210,0.14); background: rgba(140,180,210,0.03); position: relative; font-family: 'Montserrat', sans-serif; }
.corner { position: absolute; width: 12px; height: 12px; z-index: 1; pointer-events: none; }
.corner.tl { top: -1px; left: -1px; border-top: 1px solid rgba(140,180,210,0.4); border-left: 1px solid rgba(140,180,210,0.4); }
.corner.br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(140,180,210,0.4); border-right: 1px solid rgba(140,180,210,0.4); }

/* ── HEADER ── */
.post-hd { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; }
.post-user { display: flex; align-items: center; gap: 12px; }
.avatar { width: 38px; height: 38px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 16px; font-weight: 300; color: rgba(210,175,100,0.5); flex-shrink: 0; cursor: pointer; transition: border-color 0.2s; }
.avatar:hover { border-color: rgba(210,175,100,0.5); }
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.post-author { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; line-height: 1.1; margin-bottom: 2px; cursor: pointer; transition: color 0.2s; }
.post-author:hover { color: rgba(210,175,100,0.8); }
.post-time { font-size: 9px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.2); }

/* ── MENU ── */
.menu-wrap { position: relative; }
.menu-btn { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.2); padding: 4px; display: flex; align-items: center; transition: color 0.2s; }
.menu-btn:hover { color: rgba(210,175,100,0.6); }
.menu-btn svg { width: 16px; height: 16px; }
.menu-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #0f0f10; border: 1px solid rgba(210,175,100,0.2); min-width: 170px; z-index: 50; }
.menu-item { display: block; width: 100%; padding: 12px 16px; background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.4); text-align: left; transition: color 0.2s, background 0.2s; }
.menu-item:hover { background: rgba(210,175,100,0.05); color: rgba(237,232,223,0.8); }
.menu-item-danger { color: rgba(180,70,70,0.5); }
.menu-item-danger:hover { color: rgba(200,80,80,0.9); background: rgba(200,80,80,0.04); }
.menu-item-disabled { display: block; padding: 10px 14px; font-size: 12.5px; color: rgba(130,190,140,0.6); }

/* ── REPORT PANEL ── */
/* v-dialog now supplies the backdrop, centering and stacking context. */
.report-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 340px; padding: 24px; }
.report-title { font-family: 'Cormorant', serif; font-size: 19px; color: #edeae2; margin-bottom: 16px; }
.report-reason-btn { display: block; width: 100%; text-align: left; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); color: rgba(237,232,223,0.6); font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; }
.report-reason-btn:hover { border-color: rgba(200,80,80,0.35); color: rgba(200,80,80,0.85); }
.report-cancel-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: rgba(237,232,223,0.3); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px; margin-top: 6px; cursor: pointer; }

/* ── DIVIDER ── */
.post-divider { height: 1px; background: rgba(210,175,100,0.07); }

/* ── TRAVEL HEADER ── */
.travel-hd { padding: 16px 20px 6px; }
.travel-tags { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.travel-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 8.5px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; padding: 5px 10px; border: 1px solid rgba(140,180,210,0.3); color: rgba(150,190,220,0.8); }
.travel-tag svg { width: 11px; height: 11px; }
.travel-tag-badge { background: rgba(140,180,210,0.1); }
.travel-tag-city { color: rgba(210,175,100,0.75); border-color: rgba(210,175,100,0.3); }
.travel-name { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 400; color: #edeae2; line-height: 1.15; margin-bottom: 6px; }
.travel-dates { font-size: 10px; font-weight: 500; letter-spacing: 1px; color: rgba(150,190,220,0.6); text-transform: uppercase; margin-bottom: 6px; }
.travel-saved { font-size: 10px; font-weight: 500; letter-spacing: 1px; color: rgba(210,175,100,0.7); text-transform: uppercase; margin-bottom: 6px; }

/* ── BODY ── */
.post-body { padding: 4px 20px 18px; }
.post-text { font-size: 14px; font-weight: 300; color: rgba(237,232,223,0.65); line-height: 1.8; letter-spacing: 0.2px; margin-bottom: 14px; }
.post-text:last-child { margin-bottom: 0; }
.media-gallery { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px; margin-top: 4px; }
.media-gallery.single { grid-template-columns: 1fr; }
.media-gallery-item { position: relative; aspect-ratio: 1; overflow: hidden; }
.media-gallery.single .media-gallery-item { aspect-ratio: 16/10; }
.media-gallery-item img, .media-gallery-item video { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.75); }
.media-gallery-item img { cursor: pointer; }

/* ── FOOTER ── */
.post-footer { display: flex; }
.action-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px 0; background: transparent; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); transition: color 0.2s; }
.action-btn:hover { color: rgba(150,190,220,0.8); }
.action-btn + .action-btn { border-left: 1px solid rgba(210,175,100,0.07); }
.action-btn svg { width: 15px; height: 15px; }
.action-btn-primary { color: rgba(150,190,220,0.6); flex: 3; }
.action-btn-count { flex: 1; }

/* ── IMAGE OVERLAY ── */
/* v-dialog's fullscreen mode already fills and positions against the true
   viewport — this just needs to fill that space and center its contents. */
.tin-media-lightbox { width: 100%; min-height: 100vh; background: rgba(0,0,0,0.88); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 20px; padding: 24px; }
.tin-media-lightbox-img { max-width: 90vw; max-height: 80vh; object-fit: contain; }
.tin-media-lightbox-actions { display: flex; gap: 16px; }
.tin-media-lightbox-btn { width: 44px; height: 44px; border: 1px solid rgba(210,175,100,0.3); background: transparent; color: rgba(210,175,100,0.6); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.2s, color 0.2s; }
.tin-media-lightbox-btn:hover { border-color: rgba(210,175,100,0.7); color: rgba(210,175,100,1); }
.tin-media-lightbox-btn svg { width: 18px; height: 18px; }
</style>